import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Task } from '../../types';
import '../add-task/AddTask.css';

type Recurrence = Task['recurrence'];
type IntervalUnit = 'day' | 'week' | 'month' | 'year';

interface TaskRecurrenceModalProps {
    recurrence: NonNullable<Recurrence>;
    onChange: (recurrence: NonNullable<Recurrence>) => void;
    onClose: () => void;
}

export const TaskRecurrenceModal: React.FC<TaskRecurrenceModalProps> = ({ recurrence, onChange, onClose }) => {
    const { t, i18n } = useTranslation();
    const [showEndDatePicker, setShowEndDatePicker] = useState(false);
    const endDateRef = useRef<HTMLButtonElement>(null);
    const intervalUnit = recurrence.unit;
    const usesCalendar = recurrence.mode !== 'completion' && (recurrence.unit === 'week' || recurrence.unit === 'month');
    const calendarDays = recurrence.calendarDays || [];
    const weekdayLabels = i18n.language.startsWith('ro')
        ? ['D', 'L', 'M', 'M', 'J', 'V', 'S']
        : ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

    const updateIntervalUnit = (value: string) => {
        const unit = value as IntervalUnit;
        const mode = recurrence.mode;
        onChange({
            unit,
            interval: recurrence.interval || 1,
            mode,
            pattern: mode === 'completion' || unit === 'day' ? 'interval' : 'calendar',
            calendarDays: mode === 'completion' || unit === 'day'
                ? undefined
                : recurrence.unit === unit && recurrence.calendarDays?.length
                    ? recurrence.calendarDays : [unit === 'week' ? new Date().getDay() : new Date().getDate()],
            businessDays: false,
        });
    };

    const toggleCalendarDay = (day: number) => {
        if (!recurrence) return;
        onChange({
            ...recurrence,
            pattern: 'calendar',
            businessDays: false,
            calendarDays: calendarDays.includes(day)
                ? calendarDays.length === 1 ? calendarDays : calendarDays.filter((selectedDay) => selectedDay !== day)
                : [...calendarDays, day].sort((left, right) => left - right),
        });
    };

    const calendarDayOptions = recurrence.unit === 'week'
        ? [1, 2, 3, 4, 5, 6, 0]
        : Array.from({ length: 31 }, (_, index) => index + 1);
    const formatEndDate = (value: string) => new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    const endMode = recurrence?.endDate ? 'date' : recurrence?.occurrencesRemaining ? 'occurrences' : 'never';

    return createPortal(
        <div className="task-recurrence-modal-overlay" onClick={onClose}>
            <div className="task-recurrence-modal" onClick={(event) => event.stopPropagation()}>
                <h4>{t('addTask.repeat')}</h4>
                <>
                    <label>
                        {t(recurrence.mode === 'completion' ? 'addTask.after' : 'addTask.repeatEvery')}
                        <span className="task-recurrence-interval-row">
                            <input type="number" min="1" max="999" value={recurrence.interval || ''}
                                onChange={(event) => onChange({ ...recurrence, interval: event.target.value === '' ? 0 : Math.max(1, Number(event.target.value)) })}
                                onBlur={() => { if (!recurrence.interval) onChange({ ...recurrence, interval: 1 }); }} />
                            <select value={intervalUnit} onChange={(event) => updateIntervalUnit(event.target.value)}>
                                <option value="day">{t(recurrence.interval === 1 ? 'addTask.day' : 'addTask.days')}</option>
                                <option value="week">{t(recurrence.interval === 1 ? 'addTask.week' : 'addTask.weeks')}</option>
                                <option value="month">{t(recurrence.interval === 1 ? 'addTask.month' : 'addTask.months')}</option>
                                <option value="year">{t(recurrence.interval === 1 ? 'addTask.year' : 'addTask.years')}</option>
                            </select>
                        </span>
                        {recurrence.mode === 'completion' && <span className="task-recurrence-suffix">{t('addTask.fromCompletion')}</span>}
                    </label>
                    {usesCalendar && <>
                    <div className="task-recurrence-calendar-days">
                        <span>{t(recurrence.unit === 'week' ? 'addTask.weekdays' : 'addTask.monthDays')}</span>
                        <div className={recurrence.unit === 'week' ? 'task-recurrence-day-grid week' : 'task-recurrence-day-grid'}>
                            {calendarDayOptions.map((day) => <button type="button" key={day}
                                className={calendarDays.includes(day) ? 'active' : ''} onClick={() => toggleCalendarDay(day)}>
                                {recurrence.unit === 'week' ? weekdayLabels[day] : day}
                            </button>)}
                        </div>
                    </div>
                    {recurrence.unit === 'week' && <label className="task-recurrence-checkbox">
                        <input type="checkbox" checked={!!recurrence.businessDays} onChange={(event) => onChange({
                            ...recurrence,
                            pattern: 'calendar',
                            businessDays: event.target.checked,
                            calendarDays: event.target.checked ? [1, 2, 3, 4, 5] : recurrence.calendarDays,
                        })} />
                        {t('addTask.businessDays')}
                    </label>}
                    </>}
                    <label className="task-recurrence-checkbox">
                        <input type="checkbox" checked={recurrence.mode === 'completion'}
                            onChange={(event) => {
                                const mode = event.target.checked ? 'completion' : 'fixed';
                                const pattern = mode === 'completion' || recurrence.unit === 'day' || recurrence.unit === 'year' ? 'interval' : 'calendar';
                                onChange({
                                    ...recurrence,
                                    mode,
                                    pattern,
                                    calendarDays: pattern === 'calendar'
                                        ? recurrence.calendarDays?.length ? recurrence.calendarDays : [recurrence.unit === 'week' ? new Date().getDay() : new Date().getDate()]
                                        : undefined,
                                    businessDays: pattern === 'calendar' ? recurrence.businessDays : undefined,
                                });
                            }} />
                        {t('addTask.repeatAfterCompletion')}
                    </label>
                </>
                <div className="task-recurrence-end">
                    <span>{t('addTask.ends')}</span>
                    <div className="task-recurrence-end-radios">
                        <label><input type="radio" name="recurrence-end" checked={endMode === 'never'} onChange={() => onChange({ ...recurrence, endDate: undefined, occurrencesRemaining: undefined })} />{t('addTask.never')}</label>
                        <label><input type="radio" name="recurrence-end" checked={endMode === 'date'} onChange={() => onChange({ ...recurrence, endDate: recurrence.endDate || dayjs().endOf('day').toISOString(), occurrencesRemaining: undefined })} />{t('addTask.onDate')}<button ref={endDateRef} type="button" className="task-recurrence-end-date" onClick={() => setShowEndDatePicker(true)}>{recurrence.endDate ? formatEndDate(recurrence.endDate) : formatEndDate(dayjs().endOf('day').toISOString())}</button></label>
                        <label><input type="radio" name="recurrence-end" checked={endMode === 'occurrences'} onChange={() => onChange({ ...recurrence, endDate: undefined, occurrencesRemaining: recurrence.occurrencesRemaining || 1 })} />{t('addTask.afterCount')}<input type="number" min="1" max="999" value={endMode === 'occurrences' ? recurrence.occurrencesRemaining || '' : 1} onFocus={() => onChange({ ...recurrence, endDate: undefined, occurrencesRemaining: recurrence.occurrencesRemaining || 1 })} onChange={(event) => onChange({ ...recurrence, endDate: undefined, occurrencesRemaining: event.target.value === '' ? 0 : Math.max(1, Number(event.target.value)) })} onBlur={() => { if (!recurrence.occurrencesRemaining) onChange({ ...recurrence, occurrencesRemaining: 1 }); }} />{t('addTask.appearances')}</label>
                    </div>
                    <DatePicker open={showEndDatePicker} onClose={() => setShowEndDatePicker(false)} value={recurrence.endDate ? dayjs(recurrence.endDate) : dayjs()}
                        onChange={(value) => onChange({ ...recurrence, endDate: value?.endOf('day').toISOString(), occurrencesRemaining: undefined })}
                        slots={{ textField: () => null }} slotProps={{ popper: { anchorEl: endDateRef.current }, actionBar: { actions: ['clear', 'accept'] } }} />
                </div>
                <button type="button" className="task-recurrence-modal-done" onClick={onClose}>{t('addTask.done')}</button>
            </div>
        </div>,
        document.body,
    );
};
